'use client'

import type {
  FilterValueSources,
  IssuerSearchInputProps,
} from '@moment/risk-controls-feature/filter-builder/filter-value-sources'
import type { ReferenceDataField } from '@moment/trading-types/instrument-data'

import { toError } from '@moment/observability/logger'
import { useLogger } from '@moment/observability/react'
import { FilterValueSourcesProvider } from '@moment/risk-controls-feature/filter-builder/filter-value-sources'
import { type ReactNode, useEffect, useMemo } from 'react'

import { useListListsQuery } from '@/legacy-api/filter-lists-slice.gen'
import IssuerSearch from '@/shared/components/issuer-search'
import { SearchInputSize } from '@/shared/components/search-input/types'
import { referenceDataTransformationConfig } from '@/utils/presentation/instrument-data-transforms'

const loggedFilterListErrors = new WeakSet<object>()

function useSavedFilterListOptions(fieldId: string) {
  const logger = useLogger()
  const { data, isLoading, isError, error } = useListListsQuery()
  // Reports a failed saved-lists request to the app logger once per cached error, not once per row
  useEffect(() => {
    if (!isError || !error || loggedFilterListErrors.has(error)) return
    loggedFilterListErrors.add(error)
    logger.error('Failed to load saved filter lists', {
      error: toError(error),
    })
  }, [isError, error, logger])
  const options = useMemo(
    () =>
      (data?.data ?? [])
        .filter((list) => list.field.toLowerCase() === fieldId.toLowerCase())
        .map((list) => ({
          label: `${list.name} (${list.field})`,
          value: list.list_id,
        })),
    [data?.data, fieldId],
  )
  return { options, isLoading, isError }
}

function SmallIssuerSearch(props: IssuerSearchInputProps) {
  return <IssuerSearch {...props} size={SearchInputSize.SMALL} />
}

const webUiFilterValueSources: FilterValueSources = {
  formatOptionLabel: (fieldId, option) =>
    referenceDataTransformationConfig[
      fieldId as ReferenceDataField
    ]?.valueTransformation?.(option as never) ?? option,
  useFilterListOptions: useSavedFilterListOptions,
  IssuerSearchInput: SmallIssuerSearch,
}

interface WebUiFilterValueSourcesProviderProps {
  children: ReactNode
}

export function WebUiFilterValueSourcesProvider({
  children,
}: WebUiFilterValueSourcesProviderProps) {
  return (
    <FilterValueSourcesProvider value={webUiFilterValueSources}>
      {children}
    </FilterValueSourcesProvider>
  )
}
